/**
 * API Service for Gartenplanungs-App
 * Handles all communication with FastAPI backend
 */

// Empty string → relative URLs → CRA dev-proxy forwards to localhost:8000 (same-origin, no CORS).
// Set REACT_APP_API_URL=https://api.mein-gruenraum.de in production (Vercel env vars).
const API_BASE_URL = process.env.REACT_APP_API_URL || '';

/**
 * Generic fetch wrapper with error handling
 */
async function apiFetch(endpoint, options = {}) {
  const url = `${API_BASE_URL}${endpoint}`;

  // Pull headers out so caller-supplied headers merge with (not clobber) the
  // defaults. Spreading the whole `options` last would otherwise overwrite the
  // headers object — dropping Content-Type when a caller passes its own header
  // (e.g. If-Match), which makes the JSON body unparseable server-side.
  const { headers: optionHeaders, ...restOptions } = options;

  const fetchOptions = {
    ...restOptions,
    headers: {
      'Content-Type': 'application/json',
      ...optionHeaders,
    },
    credentials: 'include',
  };

  try {
    const response = await fetch(url, fetchOptions);

    // Handle non-2xx responses
    if (!response.ok) {
      if (response.status === 401) {
        window.dispatchEvent(new CustomEvent('auth:401'));
      }
      const errorData = await response.json().catch(() => ({}));
      // For 422 validation errors, detail is an array — stringify for readability
      const detail = errorData.detail;
      const message = Array.isArray(detail)
        ? detail.map(e => `${(e.loc || []).join('.')}: ${e.msg}`).join('; ')
        : detail || `API Error: ${response.status} ${response.statusText}`;
      if (response.status === 422) {
        console.error('422 Validation errors:', JSON.stringify(detail, null, 2));
      }
      // Expose the HTTP status so callers can branch on it (e.g. 412 conflict).
      const error = new Error(message);
      error.status = response.status;
      throw error;
    }

    // Return empty object for explicitly-empty responses (like 204 DELETE).
    // Don't guard on content-type — the CRA dev proxy doesn't reliably forward it,
    // and all our backend endpoints return JSON anyway.
    const contentLength = response.headers.get('content-length');
    if (contentLength === '0') {
      return {};
    }

    // Parse JSON response (or return {} if body is empty)
    const text = await response.text();
    return text ? JSON.parse(text) : {};
  } catch (error) {
    // Network errors or fetch failures
    if (error.name === 'TypeError' && error.message.includes('fetch')) {
      throw new Error('Cannot connect to backend. Is the server running?');
    }
    throw error;
  }
}

/**
 * Plants API
 */
export const plantsApi = {
  /**
   * Get all plants with optional pagination
   * @param {Object} params - Query parameters (skip, limit)
   * @returns {Promise<Array>} Array of plants
   */
  getAll: async (params = {}) => {
    const queryString = new URLSearchParams(params).toString();
    const endpoint = `/api/v1/plants/${queryString ? `?${queryString}` : ''}`;
    return apiFetch(endpoint);
  },

  /**
   * Get a single plant by ID
   * @param {number} id - Plant ID
   * @returns {Promise<Object>} Plant object
   */
  getById: async (id) => {
    return apiFetch(`/api/v1/plants/${id}`);
  },

  /**
   * Create a new plant
   * @param {Object} plantData - Plant data (name, scientific_name, etc.)
   * @returns {Promise<Object>} Created plant object
   */
  create: async (plantData) => {
    return apiFetch('/api/v1/plants/', {
      method: 'POST',
      body: JSON.stringify(plantData),
    });
  },

  /**
   * Update an existing plant
   * @param {number} id - Plant ID
   * @param {Object} plantData - Updated plant data
   * @returns {Promise<Object>} Updated plant object
   */
  update: async (id, plantData) => {
    return apiFetch(`/api/v1/plants/${id}`, {
      method: 'PUT',
      body: JSON.stringify(plantData),
    });
  },

  /**
   * Partially update a plant
   * @param {number} id - Plant ID
   * @param {Object} plantData - Partial plant data
   * @returns {Promise<Object>} Updated plant object
   */
  patch: async (id, plantData) => {
    return apiFetch(`/api/v1/plants/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(plantData),
    });
  },

  /**
   * Delete a plant
   * @param {number} id - Plant ID
   * @returns {Promise<Object>} Deletion confirmation
   */
  delete: async (id) => {
    return apiFetch(`/api/v1/plants/${id}`, {
      method: 'DELETE',
    });
  },

  /**
   * Search plants by name
   * @param {string} query - Search query
   * @returns {Promise<Array>} Array of matching plants
   */
  search: async (query) => {
    const queryString = new URLSearchParams({ q: query }).toString();
    return apiFetch(`/api/v1/plants/search/?${queryString}`);
  },

  /**
   * Get all plants with complete properties for client-side filtering
   * @param {boolean} includeInactive - Include soft-deleted plants (default: false)
   * @returns {Promise<Array>} Array of all plants with full properties
   */
  getFullCatalog: async (includeInactive = false) => {
    const params = includeInactive ? { include_inactive: 'true' } : {};
    const queryString = new URLSearchParams(params).toString();
    const endpoint = `/api/v1/plants/full${queryString ? `?${queryString}` : ''}`;
    return apiFetch(endpoint);
  },
};

/**
 * Catalog API — anonymous search telemetry (#593)
 */
export const catalogApi = {
  /**
   * Report an anonymous zero-result catalog search ("plant searched, not found").
   *
   * Fire-and-forget: this is telemetry the DB-expansion (#541) prioritizes on,
   * never an action the user waits on. Errors are swallowed so a failed report
   * can never disrupt the catalog UI. The backend stores only the normalized
   * term + a daily counter — no user/session/IP (DSGVO, #593).
   *
   * @param {string} term - The search term that returned no matches
   * @returns {Promise<void>} Resolves once the (best-effort) request settles
   */
  reportSearchMiss: async (term) => {
    try {
      await apiFetch('/api/v1/catalog/search-misses', {
        method: 'POST',
        body: JSON.stringify({ term }),
      });
    } catch {
      // Best-effort telemetry — ignore network/validation failures.
    }
  },
};

/**
 * Gardens API
 */
export const gardensApi = {
  /**
   * Create a new garden
   * @param {Object} gardenData - Garden data (name, settings, outline, etc.)
   * @returns {Promise<Object>} Created garden object with UUID
   */
  create: async (gardenData) => {
    return apiFetch('/api/v1/gardens/', {
      method: 'POST',
      body: JSON.stringify(gardenData),
    });
  },

  /**
   * Get a garden by UUID
   * @param {string} gardenId - Garden UUID
   * @returns {Promise<Object>} Garden object
   */
  getById: async (gardenId) => {
    return apiFetch(`/api/v1/gardens/${gardenId}`);
  },

  /**
   * Get all gardens with optional pagination
   * @param {Object} params - Query parameters (skip, limit)
   * @returns {Promise<Array>} Array of gardens
   */
  getAll: async (params = {}) => {
    const queryString = new URLSearchParams(params).toString();
    return apiFetch(`/api/v1/gardens/${queryString ? `?${queryString}` : ''}`);
  },

  /**
   * Partially update a garden (PATCH)
   * @param {string} gardenId - Garden UUID
   * @param {Object} data - Partial garden data to update
   * @param {Object} [options] - Extra options
   * @param {number} [options.version] - Last-known garden version. When set, sent
   *   as an `If-Match` header for optimistic locking; a stale version yields a
   *   412 error (error.status === 412) instead of silently overwriting (#496).
   * @returns {Promise<Object>} Updated garden object (includes `version`)
   */
  patch: async (gardenId, data, { version } = {}) => {
    const headers = {};
    if (version != null) headers['If-Match'] = `"${version}"`;
    return apiFetch(`/api/v1/gardens/${gardenId}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
      headers,
    });
  },

  /**
   * Best-effort page-unload flush (#482) via `navigator.sendBeacon`.
   *
   * `fetch(..., { keepalive: true })` is unreliable on unload in Firefox — it is
   * torn down with a NetworkError and the flush is lost, which since #484 (backend
   * is the authoritative reload source) means unsaved edits vanish on reload.
   * `sendBeacon` is the transport browsers deliver reliably as the document goes
   * away. It can only issue a header-less same-origin POST, so it targets the
   * dedicated `/flush` endpoint (server-side unconditional update — no `If-Match`;
   * last-write-wins is the intended unload behaviour). Cookies (auth) are sent
   * automatically for the same origin.
   *
   * Fire-and-forget: returns whether the browser accepted the beacon for queueing
   * (`false` e.g. when the ~64 KB body cap is exceeded — sanitizeBackgroundImage
   * keeps a typical payload well under it; the 30s interval / manual save remain
   * the guaranteed path).
   *
   * @param {string} gardenId - Garden UUID
   * @param {Object} data - Partial garden data to update
   * @returns {boolean} Whether the beacon was queued
   */
  flushBeacon: (gardenId, data) => {
    if (typeof navigator === 'undefined' || !navigator.sendBeacon) return false;
    const blob = new Blob([JSON.stringify(data)], { type: 'application/json' });
    return navigator.sendBeacon(
      `${API_BASE_URL}/api/v1/gardens/${gardenId}/flush`,
      blob
    );
  },

  /**
   * Delete a garden (soft delete by default)
   * @param {string} gardenId - Garden UUID
   * @returns {Promise<Object>} Deletion confirmation
   */
  delete: async (gardenId) => {
    return apiFetch(`/api/v1/gardens/${gardenId}`, {
      method: 'DELETE',
    });
  },
};

/**
 * Images API
 */
export const imagesApi = {
  /**
   * Upload an image file (multipart/form-data)
   * @param {File} file - Image file to upload
   * @returns {Promise<Object>} { imageUrl, filename, size, contentType }
   */
  upload: async (file) => {
    const formData = new FormData();
    formData.append('file', file);
    const url = `${API_BASE_URL}/api/v1/images/upload`;
    // Don't set Content-Type — browser adds multipart/form-data with boundary automatically
    const response = await fetch(url, { method: 'POST', body: formData });
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.detail || `Upload failed: ${response.status}`);
    }
    return response.json();
  },

  /**
   * Build full URL for an image from its API path
   * @param {string} imageUrl - Relative API path (e.g. "/api/v1/images/uuid.png")
   * @returns {string} Full URL
   */
  getFullUrl: (imageUrl) => `${API_BASE_URL}${imageUrl}`,

  /**
   * Delete an image file
   * @param {string} filename - Image filename
   * @returns {Promise<Object>} Deletion confirmation
   */
  delete: async (filename) => {
    return apiFetch(`/api/v1/images/${filename}`, { method: 'DELETE' });
  },
};

/**
 * Postal Codes API
 */
export const postalCodesApi = {
  /**
   * Look up location data by postal code (PLZ)
   * @param {string} zipcode - Postal code (4-5 digits, DE/AT/CH)
   * @returns {Promise<Object>} { zipcode, results, count }
   */
  lookup: async (zipcode) => {
    return apiFetch(`/api/v1/postal-codes/${zipcode}`);
  },
};

/**
 * Health check
 */
export const healthApi = {
  check: async () => {
    return apiFetch('/health');
  },
};

export default plantsApi;
